import { Injectable } from '@angular/core';
import { fromEvent, Observable, Subject, takeUntil } from 'rxjs';
import { filter } from 'rxjs/operators';
import { StaticDependenciesService } from '@cinetixx/cinetixx-ui';
import { DragState, DragStateContent, OptionsType } from '../../types';

const filterDragStart = <T = DragEvent>(options: OptionsType) => (input: Observable<Event>): Observable<T> => {
  return input.pipe(
    filter((event: unknown): event is T => {
      return event instanceof DragEvent && event.target instanceof HTMLElement && options.allowDrag(event.target) && event.target.draggable && !options.denyDrag(event.target);
    })
  );
};

@Injectable({
  providedIn: 'root'
})
export class DragDropService {

  public dragState: DragState = new Map<string, DragStateContent>();

  public init(name: string, options: OptionsType): void {
    this.dragState.set(name, {
      options,
      draggingElementsMap: new Map(),
      destroy$: new Subject<void>(),
      stateProps: {
        isSelectionActive: false,
        isDoubleClicked: false,
        mouseMoved: false,
        shiftKeyPressed: false,
        lastActiveElements: [],
        activeElements: [],
        mouseDownHostElement: null,
      },
      emitter: {
        beforeMouseDown$: new Subject(),
        afterMouseDown$: new Subject(),
        beforeMouseUp$: new Subject(),
        afterMouseUp$: new Subject(),
        beforeDrop$: new Subject(),
        afterDrop$: new Subject(),
        beforeMouseMove$: new Subject(),
        afterMoveMove$: new Subject(),
        stateChange$: new Subject(),
        dragEnter$: new Subject(),
        dragLeave$: new Subject(),
        onSelectAll$: new Subject()
      }
    });

    fromEvent<DragEvent>(StaticDependenciesService.document, 'dragstart').pipe(
      takeUntil(this.dragState.get(name).destroy$),
      filterDragStart(options),
    ).subscribe((e: DragEvent) => {
      e.dataTransfer.clearData();

      if (!((e.target as HTMLElement).parentElement.dataset['name'] === options)) {
        e.dataTransfer?.setData('text/plain', (e.target as HTMLElement).outerHTML);
        e.dataTransfer?.setData('width', (e.target as HTMLElement).offsetWidth.toString());
        e.dataTransfer?.setData('height', (e.target as HTMLElement).offsetHeight.toString());
        e.dataTransfer?.setData('cursorOffsetX', e.offsetX.toString());
        e.dataTransfer?.setData('cursorOffsetY', e.offsetY.toString());
      }
    });
  }

  public getElementBySelector(name: string, el: HTMLElement): HTMLElement {
    for (const selector of this.dragState.get(name).options?.draggableSelectors) {
      if (el.closest(selector)) {
        return el.closest(selector);
      }
    }

    return null;
  }

  public destroy(name: string): void {
    if (this.dragState.get(name)) {
      this.dragState.get(name).destroy$.next();
      this.dragState.get(name).destroy$.complete();

      for (const emitter of Object.values(this.dragState.get(name).emitter)) {
        emitter.complete();
      }

      this.dragState.delete(name);
    }
  }
}
